import { afterNextRender, Component, DestroyRef, ElementRef, HostListener, inject, Injector, input, Signal, signal, viewChild } from '@angular/core';
import { catchError, EMPTY, first, Observable } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

/**
 * Reusable "share link" trigger + mini-modal popover.
 *
 * Renders a small text-button trigger (e.g. "share link" next to "download as pdf").
 * On click, opens a compact popover anchored to the trigger showing a read-only,
 * copyable URL and a copy-to-clipboard icon button. The popover fetches the URL lazily
 * via `fetchShareUrlFn` each time it opens, so callers control how the link is produced
 * (e.g. mapping a `ShareLinkResponse.relativeUrl` to an absolute URL) and which
 * domain-specific API endpoint is used (pipe class, valve, etc.).
 *
 * Usage:
 * ```html
 * <app-share-link-button [fetchShareUrlFn]="getPipeClassShareUrl(pipeClassViewModel.id)"></app-share-link-button>
 * ```
 *
 * In the host component:
 * ```typescript
 * getPipeClassShareUrl = (pipeClassId: string): () => Observable<string> =>
 *   () => this.esraPipingAPIClient.pipeClassShareLink(pipeClassId).pipe(
 *     map(response => `${window.location.origin}/${response.relativeUrl.replace(/^\/+/, '')}`)
 *   );
 * ```
 */
@Component({
  selector: 'app-share-link-button',
  templateUrl: './share-link-button.component.html',
  styleUrls: ['./share-link-button.component.scss'],
  standalone: false
})
export class ShareLinkButtonComponent {
  /** Returns an Observable of the absolute shareable URL. Invoked each time the popover opens. */
  fetchShareUrlFn = input.required<() => Observable<string>>();

  /** Trigger button label. Defaults to 'share link'. */
  label = input<string>('share link');

  /** Bold the trigger label to match a sibling button row's font-weight (e.g. valve summary page). */
  boldLabel = input<boolean>(false);

  /** Show a small icon after the label, to match icon-suffixed sibling buttons (e.g. valve summary page). */
  showIcon = input<boolean>(false);

  /** Style the trigger like the compact `.text-button` card actions (e.g. Clone), instead of `mat-button`. Implies an icon. */
  compact = input<boolean>(false);

  private readonly snackBarService: SnackBarService = inject(SnackBarService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly injector: Injector = inject(Injector);

  private readonly trigger: Signal<ElementRef<HTMLElement> | undefined> = viewChild('trigger', { read: ElementRef });
  private readonly popoverPanel: Signal<ElementRef<HTMLElement> | undefined> = viewChild('popoverPanel', { read: ElementRef });
  private readonly shareUrlInputEl: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild('shareUrlInput', { read: ElementRef });

  isOpen = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  shareUrl = signal<string>('');
  justCopied = signal<boolean>(false);

  /**
   * Which side the popover opens toward. Defaults to 'left' (panel right-aligned to the
   * trigger) since this button is typically the rightmost item in a right-floated button row --
   * that also keeps the copy button close to where the user just clicked. Recomputed from the
   * trigger's actual position each time the popover opens, so a future placement nearer the left
   * edge of the page flips to 'right' automatically instead of overflowing off-screen.
   */
  openSide = signal<'left' | 'right'>('left');

  private copiedResetHandle: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.copiedResetHandle));
  }

  /**
   * Closes the popover on outside clicks. `cdkConnectedOverlay`'s own `overlayOutsideClick`
   * output was unreliable for this popover, so a document-level listener plus explicit
   * containment checks against both the trigger and the panel is used instead.
   */
  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!this.isOpen()) {
      return;
    }
    const clickedTrigger: boolean = this.trigger()?.nativeElement.contains(target as Node) ?? false;
    const clickedPanel: boolean = this.popoverPanel()?.nativeElement.contains(target as Node) ?? false;
    if (!clickedTrigger && !clickedPanel) {
      // Don't return focus to the trigger here: a pointer-driven outside click shouldn't
      // leave a "stuck" focus ring on the button (browsers keep it visible until some
      // other focusable element is focused).
      this.close(false);
    }
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Closes the popover. Pass `returnFocus = true` (e.g. Escape key) to restore focus to the trigger. */
  close(returnFocus: boolean = true): void {
    this.isOpen.set(false);
    this.isLoading.set(false);
    this.justCopied.set(false);
    if (returnFocus) {
      this.trigger()?.nativeElement.focus();
    }
  }

  copyToClipboard(): void {
    // No `.select()` here: selecting the readonly input would reset its scroll position
    // back to the start, undoing the scroll-to-end applied when the popover opened.
    navigator.clipboard.writeText(this.shareUrl()).then(() => {
      this.justCopied.set(true);
      clearTimeout(this.copiedResetHandle);
      this.copiedResetHandle = setTimeout(() => this.justCopied.set(false), 2000);
    }).catch(() => {
      this.snackBarService.showError('Could not copy the link. Please copy it manually.');
    });
  }

  /**
   * Picks which side the popover opens toward based on the trigger's actual position, using an
   * estimated panel width (measuring the real panel would require it to render first, causing an
   * open-then-flip flicker). Falls back to 'left' -- the default -- if neither side fully fits.
   */
  private computeOpenSide(): 'left' | 'right' {
    const triggerRect: DOMRect | undefined = this.trigger()?.nativeElement.getBoundingClientRect();
    if (!triggerRect) {
      return 'left';
    }
    const estimatedPanelWidth = 360; // share-link-input (300) + copy-button (34) + padding/gaps
    if (triggerRect.right - estimatedPanelWidth >= 0) {
      return 'left';
    }
    return triggerRect.left + estimatedPanelWidth <= window.innerWidth ? 'right' : 'left';
  }

  private open(): void {
    this.openSide.set(this.computeOpenSide());
    this.isOpen.set(true);
    this.isLoading.set(true);
    this.shareUrl.set('');

    this.fetchShareUrlFn()().pipe(
      first(),
      catchError(() => {
        this.snackBarService.showError('Unable to generate a share link. Please try again.');
        this.close();
        return EMPTY;
      })
    ).subscribe((url: string) => {
      this.shareUrl.set(url);
      this.isLoading.set(false);
      // The URL's most relevant part (pipe class/valve name) is at the end -- scroll it into view
      // once the input has rendered, instead of showing the start of a long, mostly-boilerplate URL.
      afterNextRender(() => {
        const inputEl: HTMLInputElement | undefined = this.shareUrlInputEl()?.nativeElement;
        if (inputEl) {
          inputEl.scrollLeft = inputEl.scrollWidth;
        }
      }, { injector: this.injector });
    });
  }
}
